import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useVehicleSettingsService } from "../service/vehicleSettingsService";
import { useSnackbar } from "notistack";
import { saveAs } from 'file-saver';

export const useLocationReportsOperations = () => {
  const vehicleSettingsService = useVehicleSettingsService();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Query para obtener reportes por fecha
  const useLocationReportsByDate = (date) => {
    return useQuery({
      queryKey: ['locationReports', date],
      queryFn: () => vehicleSettingsService.getLocationReportRecords(date),
      enabled: !!date, // Solo consultar cuando hay una fecha
    });
  };

  // Query para obtener reportes de un vehículo específico
  const useVehicleReportsByDate = (plate, date) => {
    return useQuery({
      queryKey: ['vehicleReports', plate, date],
      queryFn: () => vehicleSettingsService.getVehicleReportsByDate(plate, date),
      enabled: !!plate && !!date,
    });
  };

  // Mutation para crear reporte
  const useCreateLocationReport = () => {
    return useMutation({
      mutationFn: (reportData) => vehicleSettingsService.createLocationReport(reportData),
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['locationReports', variables.report_date]);
        enqueueSnackbar('Reporte creado correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al crear reporte: ${error.message}`, { variant: 'error' });
      },
    });
  };

  // Mutation para actualizar reporte
  const useUpdateLocationReport = () => {
    return useMutation({
      mutationFn: ({ id, reportData }) => vehicleSettingsService.updateLocationReport(id, reportData),
      onSuccess: (data, variables) => {
        // Invalidar consultas relevantes
        if (variables.reportData.report_date) {
          queryClient.invalidateQueries(['locationReports', variables.reportData.report_date]);
        } else {
          queryClient.invalidateQueries({ queryKey: ['locationReports'] });
        }
        enqueueSnackbar('Reporte actualizado correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al actualizar reporte: ${error.message}`, { variant: 'error' });
      },
    });
  };

  // Mutation para eliminar reporte
  const useDeleteLocationReport = () => {
    return useMutation({
      mutationFn: (id) => vehicleSettingsService.deleteLocationReport(id),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['locationReports'] });
        enqueueSnackbar('Reporte eliminado correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al eliminar reporte: ${error.message}`, { variant: 'error' });
      },
    });
  };

  // Query para obtener estadísticas mensuales
  const useMonthlyStats = (year, month) => {
    return useQuery({
      queryKey: ['locationReportStats', year, month],
      queryFn: () => vehicleSettingsService.getMonthlyStats(year, month),
      enabled: !!year && !!month,
    });
  };

  // Función para exportar reportes a Excel
  const useExportDailyReportsExcel = () => {
    return useMutation({
      mutationFn: (date) => vehicleSettingsService.exportDailyReportsExcel(date),
      onSuccess: (data, date) => {
        // Utilizar file-saver para descargar el blob
        const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Reporte-Ubicacion-${date}.xlsx`);
        enqueueSnackbar('Reporte exportado correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al exportar reporte: ${error.message}`, { variant: 'error' });
      },
    });
  };

  return {
    useLocationReportsByDate,
    useVehicleReportsByDate,
    useCreateLocationReport,
    useUpdateLocationReport,
    useDeleteLocationReport,
    useMonthlyStats,
    useExportDailyReportsExcel,
  };
};